<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const STORAGE_KEY = 'gofood_migration_notice_seen'

const isVisible = ref(false)

onMounted(() => {
  try {
    if (!localStorage.getItem(STORAGE_KEY)) {
      isVisible.value = true
      document.body.style.overflow = 'hidden'
    }
  } catch {
    // Private browsing or localStorage unavailable — show the modal anyway
    isVisible.value = true
    document.body.style.overflow = 'hidden'
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

function dismiss() {
  isVisible.value = false
  document.body.style.overflow = ''
  try {
    localStorage.setItem(STORAGE_KEY, 'true')
  } catch {
    // Ignore — modal won't persist but that's acceptable
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="migration-modal">
      <div v-if="isVisible" class="migration-overlay" @click.self="dismiss">
        <div class="migration-modal">
          <!-- Gradient Header Bar -->
          <div class="migration-header"></div>

          <!-- Content -->
          <div class="migration-body">
            <!-- SVG Animation -->
            <div class="migration-animation">
              <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Ground line -->
                <line
                  class="anim-ground"
                  x1="10"
                  y1="128"
                  x2="250"
                  y2="128"
                  stroke="#e2e8f0"
                  stroke-width="1.5"
                />

                <!-- OLD HOUSE (left side) -->
                <g class="anim-old-house">
                  <!-- Roof -->
                  <polygon
                    points="50,58 28,82 72,82"
                    stroke="#94a3b8"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linejoin="round"
                  />
                  <!-- Body -->
                  <rect
                    x="32"
                    y="82"
                    width="36"
                    height="45"
                    rx="2"
                    stroke="#94a3b8"
                    stroke-width="1.5"
                    fill="none"
                  />
                  <!-- Door -->
                  <rect
                    x="43"
                    y="106"
                    width="14"
                    height="21"
                    rx="1"
                    stroke="#94a3b8"
                    stroke-width="1"
                    fill="none"
                  />
                  <!-- Window -->
                  <rect
                    x="36"
                    y="88"
                    width="11"
                    height="11"
                    rx="1"
                    stroke="#94a3b8"
                    stroke-width="1"
                    fill="none"
                  />
                  <rect
                    x="53"
                    y="88"
                    width="11"
                    height="11"
                    rx="1"
                    stroke="#94a3b8"
                    stroke-width="1"
                    fill="none"
                  />
                </g>

                <!-- Dotted path -->
                <path
                  class="anim-path"
                  d="M 78 118 Q 130 88 182 118"
                  stroke="#cbd5e1"
                  stroke-width="1.5"
                  stroke-dasharray="5 4"
                  fill="none"
                />

                <!-- Moving boxes -->
                <rect
                  class="anim-box anim-box-1"
                  x="76"
                  y="111"
                  width="12"
                  height="10"
                  rx="2"
                  fill="#f59e0b"
                />
                <rect
                  class="anim-box anim-box-2"
                  x="76"
                  y="112"
                  width="10"
                  height="9"
                  rx="2"
                  fill="#3b82f6"
                />
                <rect
                  class="anim-box anim-box-3"
                  x="76"
                  y="112"
                  width="11"
                  height="8"
                  rx="2"
                  fill="#10b981"
                />

                <!-- Direction arrow -->
                <polygon class="anim-arrow" points="132,100 141,106 132,112" fill="#94a3b8" />

                <!-- NEW BUILDING (right side) -->
                <g class="anim-new-building">
                  <!-- Main structure -->
                  <rect
                    x="182"
                    y="52"
                    width="54"
                    height="76"
                    rx="3"
                    stroke="#283548"
                    stroke-width="2"
                    fill="#f8fafc"
                  />
                  <!-- Roof accent -->
                  <rect x="182" y="49" width="54" height="6" rx="3" fill="#283548" />
                  <!-- Windows Row 1 -->
                  <rect
                    class="anim-window anim-w1"
                    x="189"
                    y="62"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <rect
                    class="anim-window anim-w2"
                    x="207"
                    y="62"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <rect
                    class="anim-window anim-w3"
                    x="224"
                    y="62"
                    width="7"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <!-- Windows Row 2 -->
                  <rect
                    class="anim-window anim-w4"
                    x="189"
                    y="82"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <rect
                    class="anim-window anim-w5"
                    x="207"
                    y="82"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <rect
                    class="anim-window anim-w6"
                    x="224"
                    y="82"
                    width="7"
                    height="12"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1"
                    fill="transparent"
                  />
                  <!-- Door -->
                  <rect
                    x="200"
                    y="108"
                    width="16"
                    height="20"
                    rx="2"
                    stroke="#283548"
                    stroke-width="1.5"
                    fill="none"
                  />
                  <!-- Door handle -->
                  <circle cx="213" cy="119" r="1.5" fill="#283548" />
                </g>

                <!-- Celebration sparkles -->
                <path
                  class="anim-sparkle anim-s1"
                  d="M 240 44 l 2-6 2 6-6 2 6 2"
                  stroke="#10b981"
                  stroke-width="1.5"
                  fill="none"
                />
                <path
                  class="anim-sparkle anim-s2"
                  d="M 178 38 l 2-5 2 5-5 2 5 2"
                  stroke="#f59e0b"
                  stroke-width="1.5"
                  fill="none"
                />
                <path
                  class="anim-sparkle anim-s3"
                  d="M 248 72 l 1.5-4 1.5 4-4 1.5 4 1.5"
                  stroke="#3b82f6"
                  stroke-width="1.2"
                  fill="none"
                />
                <circle class="anim-sparkle anim-s4" cx="176" cy="50" r="2.5" fill="#10b981" />
                <path
                  class="anim-sparkle anim-s5"
                  d="M 220 38 l 1.5-4 1.5 4-4 1.5 4 1.5"
                  stroke="#ef4444"
                  stroke-width="1.2"
                  fill="none"
                />
              </svg>
            </div>

            <!-- Title -->
            <h2 class="migration-title">התחדשנו!</h2>

            <!-- Body text -->
            <p class="migration-text">
              המערכת שודרגה לפלטפורמה חדשה, חכמה ונוחה יותר.
              <br />
              כל הנתונים שלכם נשמרו והמערכת מוכנה לשימוש.
            </p>

            <!-- Support info -->
            <div class="migration-support">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
              <span>בכל בעיה טכנית: <a href="tel:0544221254" dir="ltr">054-422-1254</a></span>
            </div>

            <!-- Team signature -->
            <p class="migration-team">צוות סייבר פארק עומד לרשותכם</p>

            <!-- Dismiss button -->
            <button class="migration-dismiss-btn" @click="dismiss">
              <span>הבנתי</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ============================================
   Overlay
   ============================================ */
.migration-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

/* ============================================
   Modal Card
   ============================================ */
.migration-modal {
  background: var(--bg-primary, #fff);
  border-radius: var(--border-radius-xl, 24px);
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow:
    0 25px 60px -12px rgba(0, 0, 0, 0.35),
    0 10px 20px -5px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
}

/* ============================================
   Gradient Header Bar
   ============================================ */
.migration-header {
  height: 6px;
  background: var(--gradient-header, linear-gradient(135deg, #1e293b 0%, #334155 100%));
}

/* ============================================
   Body Content
   ============================================ */
.migration-body {
  padding: 36px 28px 32px;
  text-align: center;
  direction: rtl;
}

/* ============================================
   SVG Animation Container
   ============================================ */
.migration-animation {
  width: 220px;
  height: auto;
  margin: 0 auto 24px;
}

.migration-animation svg {
  width: 100%;
  height: auto;
}

/* ============================================
   Title
   ============================================ */
.migration-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
  margin: 0 0 12px;
  letter-spacing: -0.3px;
}

/* ============================================
   Body Text
   ============================================ */
.migration-text {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-secondary, #475569);
  margin: 0 0 24px;
}

/* ============================================
   Support Info
   ============================================ */
.migration-support {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 12px;
  font-size: 14px;
  color: var(--text-secondary, #475569);
  margin-bottom: 12px;
}

.migration-support svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: var(--color-success, #10b981);
}

.migration-support a {
  color: var(--color-info, #3b82f6);
  text-decoration: none;
  font-weight: 600;
  direction: ltr;
  unicode-bidi: embed;
}

.migration-support a:hover {
  text-decoration: underline;
}

/* ============================================
   Team Signature
   ============================================ */
.migration-team {
  font-size: 13px;
  color: var(--text-muted, #94a3b8);
  margin: 0 0 28px;
}

/* ============================================
   Dismiss Button
   ============================================ */
.migration-dismiss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 40px;
  background: var(--color-success, #10b981);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;
}

.migration-dismiss-btn:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.35);
}

.migration-dismiss-btn:active {
  transform: scale(0.97);
}

.migration-dismiss-btn svg {
  width: 20px;
  height: 20px;
}

/* ============================================
   Mobile Full-Screen (<768px)
   ============================================ */
@media (max-width: 767px) {
  .migration-overlay {
    padding: 0;
  }

  .migration-modal {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .migration-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
  }

  .migration-animation {
    width: 200px;
  }

  .migration-title {
    font-size: 26px;
  }

  .migration-dismiss-btn {
    width: 100%;
    max-width: 280px;
  }
}

/* ============================================
   Transition
   ============================================ */
.migration-modal-enter-active,
.migration-modal-leave-active {
  transition: opacity 0.35s ease;
}

.migration-modal-enter-active .migration-modal,
.migration-modal-leave-active .migration-modal {
  transition:
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.35s ease;
}

.migration-modal-enter-from,
.migration-modal-leave-to {
  opacity: 0;
}

.migration-modal-enter-from .migration-modal {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.migration-modal-leave-to .migration-modal {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

/* ============================================
   SVG Animations
   ============================================ */

/* Ground line — draws in */
.anim-ground {
  stroke-dasharray: 240;
  stroke-dashoffset: 240;
  animation: drawLine 1.2s ease-out 0.2s forwards;
}

/* Old house — fades and shrinks */
.anim-old-house {
  transform-origin: 50px 95px;
  animation: fadeOutShrink 2.5s ease-in-out 0.5s forwards;
}

/* Dotted path — draws in */
.anim-path {
  stroke-dasharray: 160;
  stroke-dashoffset: 160;
  animation: drawLine 1.5s ease-in-out 0.8s forwards;
}

/* Moving boxes — slide from old to new */
.anim-box {
  opacity: 0;
}

.anim-box-1 {
  animation: moveBox 2.2s ease-in-out 0.7s forwards;
}

.anim-box-2 {
  animation: moveBox 2.2s ease-in-out 1.1s forwards;
}

.anim-box-3 {
  animation: moveBox 2.2s ease-in-out 1.5s forwards;
}

/* Direction arrow */
.anim-arrow {
  opacity: 0;
  animation: fadeIn 0.5s ease-out 1.2s forwards;
}

/* New building — builds upward */
.anim-new-building {
  clip-path: inset(100% 0 0 0);
  animation: buildUp 1.4s ease-out 1.3s forwards;
}

/* Windows light up */
.anim-window {
  fill: transparent;
}

.anim-w1 {
  animation: windowGlow 0.5s ease-out 2.6s forwards;
}
.anim-w2 {
  animation: windowGlow 0.5s ease-out 2.8s forwards;
}
.anim-w3 {
  animation: windowGlow 0.5s ease-out 3s forwards;
}
.anim-w4 {
  animation: windowGlow 0.5s ease-out 3.2s forwards;
}
.anim-w5 {
  animation: windowGlow 0.5s ease-out 3.4s forwards;
}
.anim-w6 {
  animation: windowGlow 0.5s ease-out 3.6s forwards;
}

/* Sparkles — pop in at the end */
.anim-sparkle {
  opacity: 0;
  transform-origin: center;
}

.anim-s1 {
  animation: sparkle 0.6s ease-out 3s forwards;
}
.anim-s2 {
  animation: sparkle 0.6s ease-out 3.2s forwards;
}
.anim-s3 {
  animation: sparkle 0.6s ease-out 3.4s forwards;
}
.anim-s4 {
  animation: sparkle 0.6s ease-out 3.6s forwards;
}
.anim-s5 {
  animation: sparkle 0.6s ease-out 3.8s forwards;
}

/* ============================================
   Keyframes
   ============================================ */
@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes fadeOutShrink {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0.25;
    transform: scale(0.9);
  }
}

@keyframes moveBox {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 0;
  }
  8% {
    opacity: 1;
  }
  30% {
    transform: translateX(35px) translateY(-18px) rotate(8deg);
  }
  60% {
    transform: translateX(70px) translateY(-10px) rotate(-3deg);
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateX(100px) translateY(0) rotate(0deg);
    opacity: 0;
  }
}

@keyframes buildUp {
  0% {
    clip-path: inset(100% 0 0 0);
  }
  100% {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes windowGlow {
  0% {
    fill: transparent;
  }
  100% {
    fill: #fbbf24;
    fill-opacity: 0.6;
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.6;
  }
}

@keyframes sparkle {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1.4);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ============================================
   Reduced Motion
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  .anim-ground,
  .anim-old-house,
  .anim-path,
  .anim-box,
  .anim-box-1,
  .anim-box-2,
  .anim-box-3,
  .anim-arrow,
  .anim-new-building,
  .anim-window,
  .anim-w1,
  .anim-w2,
  .anim-w3,
  .anim-w4,
  .anim-w5,
  .anim-w6,
  .anim-sparkle,
  .anim-s1,
  .anim-s2,
  .anim-s3,
  .anim-s4,
  .anim-s5 {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
    clip-path: none !important;
    stroke-dashoffset: 0 !important;
    fill-opacity: 0.6;
  }

  .anim-old-house {
    opacity: 0.25 !important;
  }

  .migration-modal-enter-active .migration-modal,
  .migration-modal-leave-active .migration-modal {
    transition: none;
  }
}
</style>
